import { useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableSortLabel, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SeoReport, METRIC_COLUMNS, MetricKey, PageGroup, PageRow, isMeasured } from './seoTypes';
import { pageMetric, pagePrevMetric, sumPages, windowTotal } from './seoCompute';
import { DeltaCell, ValueDelta, Sparkline, fmtMetric, pct } from './seoFormat';

interface Props {
    report: SeoReport;
    sel: number;
}

type GroupSortCol = MetricKey | 'mom';

/** Sort key for a page in the selected month (no-data position/MoM sink to bottom of a desc sort). */
function sortVal(report: SeoReport, p: PageRow, col: GroupSortCol, i: number): number {
    if (col === 'mom') {
        const pv = pagePrevMetric(report, p, 'impr', i);
        return pv ? (pct(p.impr[i], pv) as number) : -Infinity;
    }
    if (col === 'position') return p.pos[i] > 0 ? p.pos[i] : Infinity;
    return pageMetric(p, col, i);
}

const GroupTable = ({ report, group, sel, n }: { report: SeoReport; group: SeoReport['groups'][PageGroup]; sel: number; n: number }) => {
    const [col, setCol] = useState<GroupSortCol>('impr');
    const [dir, setDir] = useState<'asc' | 'desc'>('desc');

    const hasChildren = group.pages.some((p) => p.kind === 'child');
    const active = group.pages.filter((p) => windowTotal(p) > 0);
    const zero = group.pages.filter((p) => windowTotal(p) <= 0);

    const sorted = active.slice().sort((a, b) => {
        let av = sortVal(report, a, col, sel);
        let bv = sortVal(report, b, col, sel);
        if (Number.isNaN(av)) av = -Infinity;
        if (Number.isNaN(bv)) bv = -Infinity;
        return dir === 'asc' ? av - bv : bv - av;
    });

    const onSort = (c: GroupSortCol) => {
        if (col === c) setDir(dir === 'asc' ? 'desc' : 'asc');
        else {
            setCol(c);
            setDir('desc');
        }
    };

    const metricCells = (p: PageRow, bold?: boolean) =>
        METRIC_COLUMNS.map((mc) => {
            // Same rule as the summary table: a metric that was not being
            // recorded shows "—", not 0. These are per-page rows, so fills need
            // landing-page attribution ('group' scope) to be meaningful.
            if (!isMeasured(report, mc.key, sel, 'group')) {
                return (
                    <TableCell key={mc.key} align="right">
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.disabled"
                            sx={{ fontWeight: bold ? 700 : 400 }}
                            title="Not recorded in this month — see the note under the summary table."
                        >
                            —
                        </Typography>
                    </TableCell>
                );
            }
            const v = pageMetric(p, mc.key, sel);
            const pv = isMeasured(report, mc.key, sel - 1, 'group')
                ? pagePrevMetric(report, p, mc.key, sel)
                : null;
            // Impressions has its own dedicated MoM column, so no inline delta here.
            if (mc.key === 'impr') {
                return (
                    <TableCell key={mc.key} align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: bold ? 700 : 400 }}>
                        {fmtMetric(mc.key, v)}
                    </TableCell>
                );
            }
            return (
                <TableCell key={mc.key} align="right">
                    <ValueDelta text={fmtMetric(mc.key, v)} p={pv != null ? pct(v, pv) : null} lowerBetter={mc.key === 'position'} bold={bold} />
                </TableCell>
            );
        });

    const momCell = (p: PageRow, bold?: boolean) => {
        const pv = pagePrevMetric(report, p, 'impr', sel);
        const mom = pv != null ? pct(p.impr[sel], pv) : null;
        return (
            <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400 }}>
                <DeltaCell p={mom} />
            </TableCell>
        );
    };

    return (
        <>
            <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Page</TableCell>
                            {METRIC_COLUMNS.map((c) => (
                                <TableCell key={c.key} align="right" sortDirection={col === c.key ? dir : false}>
                                    <TableSortLabel active={col === c.key} direction={col === c.key ? dir : 'desc'} onClick={() => onSort(c.key)}>
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell>Trend</TableCell>
                            <TableCell align="right" sortDirection={col === 'mom' ? dir : false}>
                                <TableSortLabel active={col === 'mom'} direction={col === 'mom' ? dir : 'desc'} onClick={() => onSort('mom')}>
                                    Impr MoM
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((p) => (
                            <TableRow key={p.label} hover>
                                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.label}>
                                    {p.label}
                                </TableCell>
                                {metricCells(p)}
                                <TableCell>
                                    <Sparkline data={p.impr} />
                                </TableCell>
                                {momCell(p)}
                            </TableRow>
                        ))}
                        {hasChildren && sorted.length > 0 && (
                            <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', bgcolor: '#fafafa' } }}>
                                <TableCell sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                                {metricCells(sumPages(sorted, n), true)}
                                <TableCell>
                                    <Sparkline data={sumPages(sorted, n).impr} />
                                </TableCell>
                                {momCell(sumPages(sorted, n), true)}
                            </TableRow>
                        )}
                        {sorted.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={METRIC_COLUMNS.length + 3}>No organic impressions in this window.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {zero.length > 0 && (
                <Accordion disableGutters elevation={0} sx={{ mt: 1, bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 0, px: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {zero.length} page{zero.length !== 1 ? 's' : ''} with no organic impressions in this window
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ columns: '280px 3', fontSize: 12, color: 'text.secondary' }}>
                            {zero.map((p) => (
                                <Box key={p.label} component="div" sx={{ breakInside: 'avoid' }}>
                                    {p.label}
                                </Box>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}
        </>
    );
};

export const SeoPageGroups = ({ report, sel }: Props) => {
    const n = report.months.length;
    return (
        <Box>
            {report.groupOrder.map((gn) => {
                const group = report.groups[gn];
                const active = group.pages.filter((p) => windowTotal(p) > 0).length;
                const hasChildren = group.pages.some((p) => p.kind === 'child');
                return (
                    <Accordion key={gn} defaultExpanded={!hasChildren} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 600 }}>
                                {gn}{' '}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    · {group.pages.length} page{group.pages.length !== 1 ? 's' : ''} · {active} with data
                                </Typography>
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <GroupTable report={report} group={group} sel={sel} n={n} />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};
